import React, { type ChangeEvent, useId, forwardRef } from 'react';

export interface DropdownOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface UserDropdownProps {
  name: string;
  label: React.ReactNode;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLSelectElement>) => void;
  value?: string | number;
  defaultValue?: string | number;

  options: DropdownOption[];
  unit?: React.ReactNode;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;

  error?: string | null;
  id?: string;
}

export const UserDropdown = forwardRef<HTMLSelectElement, UserDropdownProps>(function UserDropdown({ name, label, onChange, onBlur, value, defaultValue, options, unit, helpText, placeholder, disabled = false, error, id }, ref) {
  const autoId = useId();
  const selectId = id ?? name ?? `user-dropdown-${autoId}`;

  const hasError = !!error;

  return (
    <div className="relative">
      <div className="bg-(--bg-color) group grid w-full grid-cols-[3fr_2fr_1fr_4fr] gap-x-3 rounded-lg px-2 py-px transition-all h-max-20">
        <label htmlFor={selectId} className="peer flex items-center text-sm font-medium hover:opacity-80 cursor-pointer transition-opacity" style={{ color: hasError ? 'var(--text-color-error)' : 'var(--text-color)' }}>
          {label}
        </label>

        <span className="flex items-center justify-center text-sm opacity-70" style={{ color: 'var(--text-color)' }}>
          {unit ? <>({unit})</> : null}
        </span>

        {/* keep the 3rd column for layout */}
        <div />

        <div className="relative flex items-center">
          <select
            id={selectId}
            name={name}
            ref={ref}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            className="w-full rounded-md border px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: disabled ? 'rgba(0,0,0,0.03)' : 'white',
              color: hasError ? 'var(--text-color-error)' : 'var(--text-color)',
              borderColor: hasError ? 'var(--text-color-error)' : 'rgba(0,0,0,0.15)',
              boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none'
            }}
            title={error || undefined}
            aria-label={typeof label === 'string' ? label : undefined}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {hasError && (
            <div className="pointer-events-none absolute left-0 top-full z-1000 mt-2 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium text-white shadow-lg" style={{ backgroundColor: 'var(--text-color-error)' }}>
              <div className="absolute bottom-full left-4 border-[6px] border-transparent" style={{ borderBottomColor: 'var(--text-color-error)' }} />
              {error}
            </div>
          )}
        </div>

        {/* Hover tooltip - shows only when label is hovered */}
        {helpText ? (
          <div
            className="pointer-events-none absolute left-0 top-full z-1000 mt-2 hidden min-w-50 max-w-75 rounded-lg p-3 text-sm leading-relaxed shadow-xl peer-hover:block"
            style={{
              backgroundColor: 'var(--bg-color)',
              color: 'var(--text-color)',
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            {helpText}
          </div>
        ) : null}
      </div>
    </div>
  );
});
